import { InterventionNoteTypeReference } from 'Models/intervention/InterventionNoteTypeModel';
import InterventionStatusModel           from 'Models/intervention/InterventionStatusModel';
import OperationCategoryModel            from 'Models/intervention/OperationCategoryModel';
import OwnerSubPartitionModel            from 'Models/intervention/OwnerSubPartitionModel';
import TaskZoneModel                     from 'Models/intervention/TaskZoneModel';
import doc                               from 'decorators/doc';
import model                             from 'decorators/model';
import { Timestampable }                 from 'helpers/traits';
import { Blamable }                      from 'helpers/traits';
import { computed }                      from 'mobx';
import InterventionPrivateApiModel       from 'modelx/models/abstracts/InterventionPrivateApiModel';
import moment                      from 'moment';
import { durationFormatted }       from 'tools/dateTools';

@model.staticLabel('Intervention', false)
@model.urnResource('intervention')
@doc.path('/interventions/{?id}')
export default class InterventionModel extends Blamable(Timestampable(InterventionPrivateApiModel)) {
	public _filters: ModelFiltersExtended<{
		'anomaliesFlags.flag[between]': string;
		'anomaliesFlags.flag[gt]': string;
		'anomaliesFlags.flag[gte]': string;
		'anomaliesFlags.flag[lt]': string;
		'anomaliesFlags.flag[lte]': string;
		'createdAt[after]': string;
		'createdAt[before]': string;
		'createdAt[strictly_after]': string;
		'createdAt[strictly_before]': string;
		'exists[interventionAcks]': '0' | '1';
		'exists[interventionGroupInterventions]': '0' | '1';
		'exists[interventionOperations.taskOperation.mainTaskOperationExtras]': '0' | '1';
		'exists[interventionOperations.taskOperation.taskOperationExtras]': '0' | '1';
		'interventionDate[end]': string | Date;
		'interventionDate[start]': string | Date;
		'interventionGroupInterventions.interventionGroup': id;
		'interventionNotes.interventionNoteType': id;
		'interventionNotes.interventionNoteType.reference': InterventionNoteTypeReference;
		'interventionOperations': id;
		'interventionOperations.taskOperation': id;
		'interventionOperations.taskOperation.operation': id;
		'interventionOperations.taskOperation.operation.operationType.operationCategory': id;
		'interventionOperations.taskOperation.task': id;
		'interventionOperations.taskOperation.task.sourceUrn': Urn;
		'interventionOperations.taskOperation.task.tags[serviceUrn][tagReference]': id;
		'interventionOperations.taskOperation.task.taskGroup': id;
		'interventionOperations.taskOperation.task.taskGroup.sourceUrn': Urn;
		'interventionOperations.taskOperation.task.taskHolder': id;
		'interventionOperations.taskOperation.task.taskHolder.holderUrn': Urn;
		'interventionOperations.taskOperation.task.taskHolder.tags[serviceUrn][tagReference]': id;
		'interventionOperations.taskOperation.task.taskLevel': id;
		'interventionOperations.taskOperation.task.taskLevel.reference': InterventionTaskLevelReference;
		'interventionOperations.taskOperation.taskOperationExtras.taskOperationExtraReason': id;
		'interventionResources': id;
		'interventionResources.resource': id;
		'interventionResources.resource.ownerResource': id;
		'interventionResources.resource.ownerResource.entityUrn': Urn;
		'interventionResources.resource.resourceType': id;
		'interventionResources.resource.resourceType.reference': InterventionResourceTypeReference;
		'interventionStatus': id;
		'interventionStatus.reference': InterventionInterventionStatusReference;
		'needAck': '0' | '1';
		'notified': '0' | '1';
		'ownerSubPartition': id;
		'ownerSubPartition.partitionUrn': Urn;
		'ownerSubPartition.subPartitionUrn': Urn;
		'scheduleEndDate[after]': string | Date;
		'scheduleEndDate[before]': string | Date;
		'scheduleEndDate[strictly_after]': string | Date;
		'scheduleEndDate[strictly_before]': string | Date;
		'scheduleStartDate[after]': string | Date;
		'scheduleStartDate[before]': string | Date;
		'scheduleStartDate[strictly_after]': string | Date;
		'scheduleStartDate[strictly_before]': string | Date;
		'tags[serviceUrn][tagReference]': id;
		'taskZone': id;
		'taskZone.owner': id;
		'taskZone.owner.clientUrn': Urn;
		'taskZone.owner.companyUrn': Urn;
		'taskZone.owner.enterprisePartitionUrn': Urn;
		'taskZone.owner.enterpriseUrn': Urn;
		'taskZone.owner.ownerSubPartition': id;
		'taskZone.owner.ownerSubPartition.partitionUrn': Urn;
		'taskZone.owner.ownerSubPartition.subPartitionUrn': Urn;
		'taskZone.zipCode': string;
		'updatedAt[after]': string | Date;
		'updatedAt[before]': string | Date;
		'updatedAt[strictly_after]': string | Date;
		'updatedAt[strictly_before]': string | Date;
		'interventionOperations.taskOperation.task.tags[$:registry:service:sales][contract_iteration_item_urn]': Urn;
		'interventionOperations.taskOperation.task.tags[$:registry:service:sales][quotation_item_urn]': Urn;
		'interventionOperations.taskOperation.task.taskHolder.tags[$:registry:service:intervention][contract_urn]': Urn;
	}> = {};

	public _sorts: ModelSortsExtended<{
		'id': string;
		'interventionStatus.label': string;
		'scheduleEndDate': string;
		'scheduleStartDate': string;
		'updatedAt': string;
	}> = {};

	@doc.model(InterventionStatusModel) declare interventionStatus: InterventionStatusModel;
	@doc.boolean declare needAck: boolean;
	@doc.string declare note: string;
	@doc.string declare noteForClient: string;
	@doc.boolean declare notified: boolean;
	@doc.number declare number: number;
	@doc.string declare observations: string;
	@doc.model(OperationCategoryModel) declare operationCategory: OperationCategoryModel;
	@doc.model(OwnerSubPartitionModel) declare ownerSubPartition: OwnerSubPartitionModel;
	@doc.date declare scheduleEndDate: Moment;
	@doc.date declare scheduleStartDate: Moment;
	@doc.model(TaskZoneModel) declare taskZone: TaskZoneModel;
	@doc.number declare value: number;

	@computed get displayDate(): string {
		const startDate = moment(this.scheduleStartDate);
		const endDate = moment(this.scheduleEndDate);

		return `${startDate.format('L')} de ${startDate.format('LT')} à ${endDate.format('LT')}`;
	}

	@computed
	public get title() {
		return `${this.displayDate} (${this.number})`;
	}

	@computed
	public get durationFormatted(): string {
		return durationFormatted(this.scheduleStartDate, this.scheduleEndDate, [0, 5]);
	}

	@computed get durationMinutes() {
		return this.scheduleEndDate.diff(this.scheduleStartDate, 'minutes');
	}

	@computed
	public get statusId(): id {
		return this.get('interventionStatus.id', 0);
	}

	@computed
	public get taskZoneId(): id {
		return this.get('taskZone.id', 0);
	}

	@computed
	public get interventionStatusId(): id {
		return this.get('interventionStatus.id');
	}

	public get isMultiDay() {
		return !this.scheduleStartDate.isSame(this.scheduleEndDate, 'day');
	}

	@model.nestedResolvable()
	public get subPartition() {
		return this.ownerSubPartition.subPartition;
	}

	@computed
	public get fiNumber() {
		return this.number.toString().padStart(6, '0');
	}
}